const READING_LIST_KEY = 'opacReadingList';

const toArray = (value) => (Array.isArray(value) ? value : []);

export function loadReadingList() {
  try {
    const raw = localStorage.getItem(READING_LIST_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    return toArray(parsed);
  } catch (error) {
    console.error('Failed to load reading list', error);
    return [];
  }
}

export function saveReadingList(list) {
  try {
    localStorage.setItem(READING_LIST_KEY, JSON.stringify(toArray(list)));
  } catch (error) {
    console.error('Failed to save reading list', error);
  }
}

export function isInReadingList(biblioId) {
  return loadReadingList().some((book) => String(book.biblio_id) === String(biblioId));
}

export function addToReadingList(book) {
  if (!book?.biblio_id) {
    return loadReadingList();
  }

  const list = loadReadingList();
  const alreadyExists = list.some((entry) => String(entry.biblio_id) === String(book.biblio_id));
  if (alreadyExists) {
    return list;
  }

  const sanitizedBook = {
    biblio_id: book.biblio_id,
    title: book.title || 'Untitled',
    author: book.author || '',
    isbn: book.isbn || '',
    copyrightdate: book.copyrightdate || '',
  };

  const nextList = [...list, sanitizedBook];
  saveReadingList(nextList);
  return nextList;
}

export function removeFromReadingList(biblioId) {
  const nextList = loadReadingList().filter((book) => String(book.biblio_id) !== String(biblioId));
  saveReadingList(nextList);
  return nextList;
}

export function clearReadingList() {
  saveReadingList([]);
  return [];
}

const sanitizeForBibtexKey = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
const splitAuthors = (author = '') => String(author)
  .split(/\s+and\s+|;/i)
  .map((part) => part.trim())
  .filter(Boolean);
const firstIsbn = (isbn = '') => String(isbn)
  .split('|')[0]
  .replace(/[()]/g, ' ')
  .trim();

const toBibtexEntry = (book) => {
  const year = /^\d{4}$/.test(String(book.copyrightdate || '')) ? String(book.copyrightdate) : '';
  const authorToken = sanitizeForBibtexKey((book.author || '').split(',')[0] || 'unknown');
  const yearToken = year || 'nd';
  const key = `${authorToken}${yearToken}${book.biblio_id}`;

  return [
    `@book{${key},`,
    `  title = {${book.title || 'Untitled'}},`,
    `  author = {${book.author || 'Unknown'}},`,
    `  year = {${year || 'n.d.'}},`,
    `  isbn = {${book.isbn || ''}},`,
    `  note = {Koha Biblionumber: ${book.biblio_id}}`,
    `}`,
  ].join('\n');
};

const toRisEntry = (book) => {
  const year = /^\d{4}$/.test(String(book.copyrightdate || '')) ? String(book.copyrightdate) : '';
  const authors = splitAuthors(book.author);
  const isbn = firstIsbn(book.isbn);
  const lines = [
    'TY  - BOOK',
    `TI  - ${book.title || 'Untitled'}`,
  ];

  if (authors.length) {
    authors.forEach((author) => lines.push(`AU  - ${author}`));
  } else {
    lines.push('AU  - Unknown');
  }

  if (year) {
    lines.push(`PY  - ${year}///`);
  }

  if (isbn) {
    lines.push(`SN  - ${isbn}`);
  }

  lines.push(`ID  - KOHA-${book.biblio_id}`);
  lines.push(`N1  - Koha Biblionumber: ${book.biblio_id}`);
  lines.push('ER  -');

  return lines.join('\r\n');
};

export function generateBibtex(list) {
  return toArray(list).map((book) => toBibtexEntry(book)).join('\n\n');
}

export function generateRis(list) {
  const entries = toArray(list).map((book) => toRisEntry(book));
  return entries.length ? `${entries.join('\r\n\r\n')}\r\n` : '';
}

export function downloadTextFile(filename, content, mimeType) {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}
